import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Calendar as CalIcon, Clock, Building2, Plus, X } from "lucide-react";

export default function Calendar() {
  const [events, setEvents] = useState([]);
  const [apps, setApps] = useState([]);
  const [showSchedule, setShowSchedule] = useState(false);
  const [form, setForm] = useState({ application_id: "", interview_at: "", notes: "" });

  const load = () => {
    api.get("/calendar/events").then((r) => setEvents(r.data));
    api.get("/applications").then((r) => setApps(r.data));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/calendar/schedule", form);
    setShowSchedule(false);
    setForm({ application_id: "", interview_at: "", notes: "" });
    load();
  };

  const grouped = groupByMonth(events);
  const upcoming = events.filter((e) => new Date(e.interview_at || e.updated_at) > new Date()).length;

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="grid md:grid-cols-3 gap-3">
        <Stat label="Upcoming interviews" value={upcoming} accent="text-cyan-300" />
        <Stat label="Total scheduled" value={events.length} accent="text-violet-300" />
        <Stat label="Applications open" value={apps.filter(a => !["Rejected", "Offer"].includes(a.status)).length} accent="text-emerald-300" />
      </div>

      <div className="jr-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CalIcon className="w-5 h-5 text-cyan-300" />
            <div className="font-semibold text-white">Interview Schedule</div>
          </div>
          <button onClick={() => setShowSchedule(true)} className="jr-btn-primary text-sm" data-testid="schedule-btn">
            <Plus className="w-4 h-4 inline mr-1" /> Schedule Interview
          </button>
        </div>

        {events.length === 0 ? (
          <div className="text-center text-zinc-500 py-8 text-sm">
            No interviews yet. When you land one, add it here so Curcompass can prep you (mock interview 24h before).
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(grouped).map(([month, list]) => (
              <div key={month}>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">{month}</div>
                <div className="space-y-2">
                  {list.map((e) => (
                    <div key={e.application_id} className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.03]" data-testid="event-row">
                      <div className="w-11 h-11 rounded-lg bg-cyan-500/15 grid place-items-center text-cyan-200">
                        <CalIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-white truncate">{e.job_title}</div>
                        <div className="text-xs text-zinc-400 flex items-center gap-2">
                          <Building2 className="w-3 h-3" /> {e.company}
                          <Clock className="w-3 h-3 ml-2" /> {formatDT(e.interview_at || e.updated_at)}
                        </div>
                        {e.interview_notes && <div className="text-xs text-zinc-500 mt-1">📝 {e.interview_notes}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showSchedule && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur grid place-items-center p-4">
          <form onSubmit={submit} className="jr-card w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <div className="font-bold text-white">Schedule an Interview</div>
              <button type="button" onClick={() => setShowSchedule(false)} className="jr-btn-ghost p-1.5"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Application *</label>
                <select required value={form.application_id} onChange={(e) => setForm({ ...form, application_id: e.target.value })} className="jr-input" data-testid="app-select">
                  <option value="" className="bg-[#161635]">Choose the job...</option>
                  {apps.map((a) => (
                    <option key={a.application_id} value={a.application_id} className="bg-[#161635]">
                      {a.job_title} @ {a.company}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Date & time *</label>
                <input required type="datetime-local" value={form.interview_at} onChange={(e) => setForm({ ...form, interview_at: e.target.value })} className="jr-input" data-testid="dt-input" />
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Notes</label>
                <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="jr-input" placeholder="Round type, interviewer name, prep topics..." />
              </div>
              <button type="submit" className="jr-btn-primary w-full" data-testid="save-schedule">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="jr-card">
      <div className="text-xs text-zinc-400">{label}</div>
      <div className={`text-3xl font-bold mt-1 ${accent}`}>{value}</div>
    </div>
  );
}

function groupByMonth(events) {
  const g = {};
  events.forEach((e) => {
    const d = new Date(e.interview_at || e.updated_at || Date.now());
    const key = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    (g[key] = g[key] || []).push(e);
  });
  return g;
}

function formatDT(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}
